import { supabase } from '@/lib/supabase';

const BILLING_SUMMARY_FUNCTION = 'billing-summary';
const BILLING_TOPUP_QUOTE_FUNCTION = 'billing-topup-quote';
const BILLING_RENEWAL_QUOTE_FUNCTION = 'billing-renewal-quote';
const BILLING_CREATE_CHECKOUT_FUNCTION = 'billing-create-checkout';
const BILLING_CONFIRM_CHECKOUT_FUNCTION = 'billing-confirm-checkout';
const BILLING_INVOICES_FUNCTION = 'billing-invoices';
const BILLING_SEAT_HISTORY_FUNCTION = 'billing-seat-history';

export type BillingSummary = {
  access_expires_on: string | null;
  active_students: number;
  available_seats: number;
  billing_account_id: string | null;
  billing_interval: 'monthly' | 'yearly' | null;
  billing_status: 'active' | 'grace_period' | 'expired' | 'suspended' | null;
  can_activate_student: boolean;
  currency_code: string | null;
  failure_reason: string | null;
  grace_period_days: number | null;
  in_grace_period: boolean;
  min_topup_seats: number | null;
  purchased_seats: number;
  renewal_anchor_date: string | null;
  school_id: string;
  topup_seat_multiple: number | null;
};

export type TopupQuote = {
  annual_unit_price_minor: number;
  currency_code: string | null;
  prorated_unit_price_minor: number;
  remaining_months: number;
  renewal_anchor_date: string;
  requested_seats: number;
  school_id: string;
  subtotal_minor: number;
};

export type RenewalQuote = {
  billing_interval: 'monthly' | 'yearly';
  currency_code: string;
  price_book_id: string;
  renewal_anchor_date: string | null;
  renewal_seats: number;
  school_id: string;
  subtotal_minor: number;
  unit_price_minor: number;
};

export type BillingPlanCode = 'launch_50' | 'growth_100' | 'scale_200';

export type BillingPlan = {
  amount_minor: number;
  code: BillingPlanCode;
  currency_code: 'INR';
  description: string;
  headline: string;
  name: string;
  seats: number;
};

export async function fetchBillingSummary() {
  const { data, error } = await supabase.functions.invoke(BILLING_SUMMARY_FUNCTION, {
    body: {},
  });

  if (error) throw error;

  return (data?.summary as BillingSummary | undefined) ?? null;
}

export async function fetchTopupQuote(requestedSeats: number) {
  const { data, error } = await supabase.functions.invoke(BILLING_TOPUP_QUOTE_FUNCTION, {
    body: {
      requestedSeats,
    },
  });

  if (error) throw error;

  return (data?.quote as TopupQuote | undefined) ?? null;
}

export async function fetchRenewalQuote(renewalSeats: number) {
  const { data, error } = await supabase.functions.invoke(BILLING_RENEWAL_QUOTE_FUNCTION, {
    body: {
      renewalSeats,
    },
  });

  if (error) throw error;

  return (data?.quote as RenewalQuote | undefined) ?? null;
}

export async function createBillingCheckout(input: {
  provider: 'manual' | 'razorpay' | 'stripe';
  purchaseType: 'renewal' | 'topup';
  renewalSeats?: number;
  requestedSeats?: number;
}) {
  const { data, error } = await supabase.functions.invoke(BILLING_CREATE_CHECKOUT_FUNCTION, {
    body: input,
  });

  if (error) throw error;

  return data?.checkout ?? null;
}

export async function confirmBillingCheckout(input: {
  paymentId: string;
  providerOrderId?: string;
  providerPaymentId?: string;
  providerSignature?: string;
}) {
  const { data, error } = await supabase.functions.invoke(BILLING_CONFIRM_CHECKOUT_FUNCTION, {
    body: input,
  });

  if (error) throw error;

  return data ?? null;
}

export async function fetchBillingInvoices(limit = 20) {
  const { data, error } = await supabase.functions.invoke(BILLING_INVOICES_FUNCTION, {
    body: { limit },
  });

  if (error) throw error;

  return {
    invoices: (data?.invoices as unknown[] | undefined) ?? [],
    payments: (data?.payments as unknown[] | undefined) ?? [],
  };
}

export async function fetchSeatHistory(limit = 50) {
  const { data, error } = await supabase.functions.invoke(BILLING_SEAT_HISTORY_FUNCTION, {
    body: { limit },
  });

  if (error) throw error;

  return (data?.events as unknown[] | undefined) ?? [];
}

export async function fetchBillingPlanCatalog() {
  const { data, error } = await supabase.functions.invoke('billing-plan-catalog', {
    body: {},
  });

  if (error) throw error;

  return {
    plans: (data?.plans as BillingPlan[] | undefined) ?? [],
    razorpay_key_id: (data?.razorpay_key_id as string | null | undefined) ?? null,
  };
}

export async function createBillingPlanCheckout(planCode: BillingPlanCode) {
  const { data, error } = await supabase.functions.invoke('billing-create-plan-checkout', {
    body: {
      planCode,
    },
  });

  if (error) throw error;

  return data?.checkout ?? null;
}

export async function confirmBillingPlanCheckout(input: {
  paymentId: string;
  providerOrderId: string;
  providerPaymentId: string;
  providerSignature: string;
}) {
  const { data, error } = await supabase.functions.invoke(
    'billing-confirm-plan-checkout',
    {
      body: input,
    }
  );

  if (error) throw error;

  return data ?? null;
}
